import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface AudienceSegment {
  id: string
  segment: string
  size: number
  engagement_rate: number
  conversion_rate: number
  avg_deal_value: number
  growth_rate: number
  channels: string[]
}

interface BehaviorPattern {
  id: string
  behavior: string
  percentage: number
  avg_sessions: number
  avg_pages: number
  time_on_site: number
  conversion_likelihood: number
}

interface Statistics {
  total_audience: number
  active_segments: number
  avg_engagement_rate: number
  top_segment: string
  highest_converting: string
  audience_growth: number
}

export default function AudienceInsightsPage() {
  const [segments, setSegments] = useState<AudienceSegment[]>([])
  const [behaviors, setBehaviors] = useState<BehaviorPattern[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [segmentsRes, behaviorsRes, statsRes] = await Promise.all([
        fetch('/api/audienceinsights/segments'),
        fetch('/api/audienceinsights/behaviors'),
        fetch('/api/audienceinsights/statistics')
      ])
      if (segmentsRes.ok) setSegments(await segmentsRes.json())
      if (behaviorsRes.ok) setBehaviors(await behaviorsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching audience insights data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    return `${mins}m`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Audience Insights</h1>
          <p className="mt-2 text-gray-600">Phase 250 - Audience segmentation and behavior</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Audience</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_audience)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Segments</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.active_segments}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Engagement Rate</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_engagement_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Top Segment</p>
              <p className="text-xl font-bold text-green-600">{statistics.top_segment}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Best Converting</p>
              <p className="text-xl font-bold text-orange-600">{statistics.highest_converting}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Growth</p>
              <p className="text-2xl font-bold text-green-600">+{statistics.audience_growth}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Audience Segments</h2>
          </div>
          <div className="p-6 space-y-4">
            {segments.map((segment) => (
              <div key={segment.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-gray-900">{segment.segment}</span>
                  <span className="text-lg font-bold text-green-600">{segment.conversion_rate}% CVR</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {segment.channels.map((channel, idx) => (
                    <span key={idx} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{channel}</span>
                  ))}
                </div>
                <div className="grid grid-cols-5 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Size</p>
                    <p className="font-medium">{formatNumber(segment.size)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Engagement</p>
                    <p className="font-medium text-purple-600">{segment.engagement_rate}%</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Avg Deal</p>
                    <p className="font-medium text-blue-600">{formatCurrency(segment.avg_deal_value)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Growth</p>
                    <p className="font-medium text-green-600">+{segment.growth_rate}%</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Potential</p>
                    <p className="font-medium text-orange-600">{formatCurrency(segment.size * segment.conversion_rate / 100 * segment.avg_deal_value)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Behavior Patterns</h2>
          </div>
          <div className="p-6 space-y-4">
            {behaviors.map((behavior) => (
              <div key={behavior.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{behavior.behavior}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{behavior.percentage}% of audience</span>
                    <span className="text-lg font-bold text-green-600">{behavior.conversion_likelihood}%</span>
                  </div>
                </div>
                <div className="h-2 bg-gray-200 rounded-full mb-3">
                  <div className="h-2 bg-green-500 rounded-full" style={{width: `${behavior.conversion_likelihood}%`}}></div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Avg Sessions</p>
                    <p className="font-medium">{behavior.avg_sessions}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Avg Pages</p>
                    <p className="font-medium text-blue-600">{behavior.avg_pages}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Time on Site</p>
                    <p className="font-medium text-purple-600">{formatTime(behavior.time_on_site)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Conversion</p>
                    <p className="font-medium text-green-600">{behavior.conversion_likelihood}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
